import { useEffect, useState } from "react";
import api, { fmtINR, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { CreditCard, Copy, Landmark, Smartphone, Clock, CheckCircle2, XCircle } from "lucide-react";

const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  return Array.isArray(d) ? d.map((x) => x.msg).join(" · ") : d || "Failed";
};
const STATUS_TONE = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected: "bg-red-50 text-red-700 border-red-200",
};

export default function Subscription() {
  const [data, setData] = useState(null);
  const load = async () => {
    try { setData((await api.get("/subscription/me")).data); }
    catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); }, []);
  if (!data) return <div className="text-sm text-slate-500" data-testid="subscription-loading">Loading…</div>;
  const pending = data.requests.find((r) => r.status === "pending");

  return (
    <div className="space-y-5 max-w-5xl" data-testid="subscription-page">
      <div>
        <h1 className="font-display text-2xl font-semibold flex items-center gap-2">
          <CreditCard className="w-6 h-6 text-emerald-700" /> Subscription & Renewal
        </h1>
        <p className="text-xs text-slate-500 mt-0.5">Pay via UPI or bank transfer, then submit the transaction reference. Vyomra verifies and extends your plan.</p>
      </div>
      <CurrentPlan data={data} />
      <div className="grid md:grid-cols-2 gap-5">
        <PayeeCard payee={data.payee} />
        {pending
          ? <PendingCard r={pending} />
          : <RenewForm plans={data.plans} currentPlanId={data.subscription.plan_id} onDone={load} />}
      </div>
      <History rows={data.requests} />
    </div>
  );
}

function CurrentPlan({ data }) {
  const s = data.subscription;
  const d = data.days_left;
  const tone = d === null ? "text-slate-700" : d < 0 ? "text-red-600" : d <= 7 ? "text-amber-600" : "text-emerald-700";
  return (
    <div className="bg-white border rounded-lg p-4 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="subscription-current">
      <Info label="Plan" value={s.plan_name || "Trial"} testid="sub-plan-name" />
      <Info label="Status" value={(data.hospital.status || "").toUpperCase()} testid="sub-status" />
      <Info label="Valid till" value={s.subscription_end ? fmtDate(s.subscription_end) : "—"} testid="sub-end" />
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Days left</div>
        <div className={`text-lg font-semibold font-mono ${tone}`} data-testid="sub-days-left">{d === null ? "—" : d < 0 ? `Expired ${-d}d ago` : d}</div>
      </div>
    </div>
  );
}

function Info({ label, value, testid }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <div className="text-lg font-semibold" data-testid={testid}>{value}</div>
    </div>
  );
}

function PayeeCard({ payee }) {
  const copy = (v) => { navigator.clipboard?.writeText(v); toast.success("Copied"); };
  const rows = [
    ["UPI ID", payee.upi_id, Smartphone], ["Account name", payee.account_name, Landmark],
    ["Bank", payee.bank_name, Landmark], ["Account no.", payee.account_no, Landmark], ["IFSC", payee.ifsc, Landmark],
  ].filter(([, v]) => v);
  return (
    <div className="bg-white border rounded-lg p-4 space-y-3" data-testid="subscription-payee">
      <div className="font-semibold text-sm">Pay to Vyomra Technologies</div>
      {rows.length === 0 && <div className="text-xs text-slate-500" data-testid="payee-missing">Payment details not configured yet — contact Vyomra support.</div>}
      {rows.map(([label, v, Icon]) => (
        <div key={label} className="flex items-center justify-between gap-2 text-sm border-b last:border-0 pb-2">
          <span className="flex items-center gap-2 text-slate-500"><Icon className="w-4 h-4" />{label}</span>
          <span className="flex items-center gap-2 font-mono">{v}
            <button onClick={() => copy(v)} data-testid={`payee-copy-${label.replace(/\W+/g, "-").toLowerCase()}`} className="text-slate-400 hover:text-emerald-700"><Copy className="w-3.5 h-3.5" /></button>
          </span>
        </div>
      ))}
      {payee.instructions && <p className="text-xs text-slate-500 whitespace-pre-line">{payee.instructions}</p>}
    </div>
  );
}

function PendingCard({ r }) {
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-2" data-testid="subscription-pending">
      <div className="flex items-center gap-2 font-semibold text-amber-800"><Clock className="w-4 h-4" /> Renewal under review</div>
      <div className="text-sm text-amber-900">{r.plan_name} · {r.cycles} × {r.billing_cycle} · <b>{fmtINR(r.amount)}</b></div>
      <div className="text-xs text-amber-800 font-mono">Ref: {r.reference} · Paid {r.paid_on}</div>
      <p className="text-xs text-amber-700">Vyomra will verify the payment and extend your subscription shortly.</p>
    </div>
  );
}

function RenewForm({ plans, currentPlanId, onDone }) {
  const [f, setF] = useState({ plan_id: currentPlanId || plans[0]?.id || "", cycles: 1, mode: "upi", reference: "", paid_on: new Date().toISOString().slice(0, 10), note: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const plan = plans.find((p) => p.id === f.plan_id);
  const total = plan ? plan.unit_price * (Number(f.cycles) || 0) : 0;
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/subscription/renewals", { ...f, cycles: Number(f.cycles) });
      toast.success("Renewal submitted for verification");
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="bg-white border rounded-lg p-4 space-y-3" data-testid="subscription-renew-form">
      <div className="font-semibold text-sm">Submit payment</div>
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <Label className="text-xs">Plan</Label>
          <select value={f.plan_id} onChange={(e) => set("plan_id", e.target.value)} data-testid="renew-plan-select" className="w-full h-9 border rounded-md px-2 text-sm">
            {plans.map((p) => <option key={p.id} value={p.id}>{p.name} — {fmtINR(p.unit_price)}/{p.billing_cycle}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs">No. of {plan?.billing_cycle || "cycle"} periods</Label>
          <Input type="number" min={1} max={24} value={f.cycles} onChange={(e) => set("cycles", e.target.value)} data-testid="renew-cycles-input" />
        </div>
        <div>
          <Label className="text-xs">Mode</Label>
          <select value={f.mode} onChange={(e) => set("mode", e.target.value)} data-testid="renew-mode-select" className="w-full h-9 border rounded-md px-2 text-sm">
            <option value="upi">UPI</option><option value="bank_transfer">Bank transfer (NEFT/IMPS/RTGS)</option>
          </select>
        </div>
        <div>
          <Label className="text-xs">{f.mode === "upi" ? "UPI transaction ID / UTR" : "Bank UTR / reference"}</Label>
          <Input value={f.reference} onChange={(e) => set("reference", e.target.value)} placeholder="e.g. 412345678901" data-testid="renew-reference-input" />
        </div>
        <div>
          <Label className="text-xs">Paid on</Label>
          <Input type="date" value={f.paid_on} onChange={(e) => set("paid_on", e.target.value)} data-testid="renew-paid-on-input" />
        </div>
        <div className="col-span-2">
          <Label className="text-xs">Note (optional)</Label>
          <Input value={f.note} onChange={(e) => set("note", e.target.value)} data-testid="renew-note-input" />
        </div>
      </div>
      <div className="flex items-center justify-between pt-1">
        <div className="text-sm">Amount payable: <b className="font-mono text-emerald-700" data-testid="renew-total">{fmtINR(total)}</b></div>
        <Button onClick={submit} disabled={busy || f.reference.trim().length < 4 || !f.plan_id} className="bg-emerald-700 hover:bg-emerald-800" data-testid="renew-submit-btn">
          {busy ? "Submitting…" : "Submit for verification"}
        </Button>
      </div>
    </div>
  );
}

function History({ rows }) {
  if (!rows.length) return null;
  return (
    <div className="bg-white border rounded-lg overflow-x-auto" data-testid="subscription-history">
      <div className="px-4 py-2 font-semibold text-sm border-b">Payment history</div>
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-xs text-slate-500"><tr>
          {["Submitted", "Plan", "Amount", "Mode", "Reference", "Status"].map((h) => <th key={h} className="text-left px-3 py-2">{h}</th>)}
        </tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t" data-testid={`renewal-row-${r.id}`}>
              <td className="px-3 py-2">{fmtDate(r.created_at)}</td>
              <td className="px-3 py-2">{r.plan_name} × {r.cycles}</td>
              <td className="px-3 py-2 font-mono">{fmtINR(r.amount)}</td>
              <td className="px-3 py-2">{r.mode === "upi" ? "UPI" : "Bank"}</td>
              <td className="px-3 py-2 font-mono">{r.reference}</td>
              <td className="px-3 py-2">
                <Badge variant="outline" className={STATUS_TONE[r.status]}>
                  {r.status === "approved" ? <CheckCircle2 className="w-3 h-3 mr-1" /> : r.status === "rejected" ? <XCircle className="w-3 h-3 mr-1" /> : <Clock className="w-3 h-3 mr-1" />}
                  {r.status}
                </Badge>
                {r.reject_reason && <div className="text-[11px] text-red-600 mt-0.5">{r.reject_reason}</div>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
